// Operator › Agent health › Opportunities (SKILLSMITH-OPPORTUNITIES, founder 2026-10-30): the
// helper sales pipeline, platform super admins only (the API answers 404 to anyone else; the
// client never sees it). Skillsmith files an opportunity when a DE keeps failing, works outside
// its helpers or gets corrected; a super admin drafts and sends the offer email, sends a paid
// helper quote, and walks it Suggested → Offered → Accepted / Declined → Added. Row actions are
// hover icons; each opens a modal and the page stays put.
import { ApiError, type OperationOutput } from "@companyos/sdk";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Badge,
  Button,
  Checkbox,
  CompanyTile,
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  EmptyState,
  Field,
  FieldError,
  FieldLabel,
  InfoTip,
  Input,
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
  Label,
  NativeSelect,
  NativeSelectOption,
  Textarea,
  toast,
} from "@companyos/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowRightIcon,
  BanIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  LightbulbIcon,
  MailIcon,
  PackageCheckIcon,
  ReceiptIcon,
  ThumbsDownIcon,
  ThumbsUpIcon,
  Undo2Icon,
  XIcon,
} from "lucide-react";
import { type FormEvent, Fragment, type ReactNode, useId, useState } from "react";
import type { Api } from "../../api/client";
import { centsToDollarsText, parseDollarsToCents } from "../billing/money";
import { formatMoney } from "../format";
import {
  type OpportunityFilters,
  opportunitiesQuery,
  opportunityQuery,
  opsKeys,
} from "../ops-queries";
import { OpsBody, RowAction, RowActions, RowSub } from "../ops-ux";
import { LoadError, Loading } from "../screen-parts";

type Pipeline = OperationOutput<"operator.opportunities.list">;
export type Opportunity = Pipeline["data"][number];
type Stage = Opportunity["stage"];
type Quote = NonNullable<Opportunity["helper_offer"]>;
type Move = "suggested" | "dismissed" | "accepted" | "declined" | "added";

export const STAGE_LABEL: Record<Stage, string> = {
  suggested: "Suggested",
  offered: "Offered",
  accepted: "Accepted",
  declined: "Declined",
  added: "Added",
  dismissed: "Dismissed",
};

const STAGE_BADGE = {
  suggested: "info",
  offered: "accent",
  accepted: "success",
  declined: "neutral",
  added: "success",
  dismissed: "outline",
} as const satisfies Record<Stage, string>;

const QUOTE_LABEL: Record<Quote["status"], string> = {
  open: "Quote waiting",
  building: "Building",
  added: "Added",
  declined: "Declined",
  canceled: "Canceled",
};

const CONFIDENCE: Record<Opportunity["confidence"], string> = {
  high: "High confidence",
  medium: "Medium confidence",
  low: "Low confidence",
};

const STAGES = ["suggested", "offered", "accepted", "declined", "added", "dismissed"] as const;

const when = (iso: string) =>
  new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });

/** `run_failures` → "Run failures". */
const words = (s: string) => {
  const t = s.replace(/_/g, " ");
  return t.charAt(0).toUpperCase() + t.slice(1);
};

const errorText = (e: unknown, fallback: string) =>
  e instanceof ApiError ? (e.problem?.detail ?? e.problem?.title ?? fallback) : fallback;

/** "$500.00 + $99.00/mo", or one of the two. */
export function quotePrice(q: Pick<Quote, "one_off_minor" | "monthly_minor" | "currency">) {
  return [
    q.one_off_minor > 0 ? formatMoney(q.one_off_minor, q.currency) : null,
    q.monthly_minor > 0 ? `${formatMoney(q.monthly_minor, q.currency)}/mo` : null,
  ]
    .filter(Boolean)
    .join(" + ");
}

type Dialogs =
  | { kind: "draft"; opp: Opportunity }
  | { kind: "quote"; opp: Opportunity }
  | { kind: "stage"; opp: Opportunity; stage: "accepted" | "declined" | "added" }
  | { kind: "cancel"; opp: Opportunity; quote: Quote };

/** The pipeline page: counts and revenue, filters, the opportunities. */
export function OpportunitiesView({ api, companyId }: { api: Api; companyId?: string }) {
  const [company, setCompany] = useState(companyId ?? "");
  const [stage, setStage] = useState<Stage | "">("");
  const filters: OpportunityFilters = {
    ...(company ? { company_id: company } : {}),
    ...(stage ? { stage } : {}),
  };
  const q = useQuery(opportunitiesQuery(api, filters));
  const [dialog, setDialog] = useState<Dialogs | null>(null);
  const actions = useOpportunityActions(api);
  if (q.isPending)
    return (
      <OpsBody>
        <Loading label="Loading opportunities" />
      </OpsBody>
    );
  if (q.isError)
    return (
      <OpsBody>
        {q.error instanceof ApiError && q.error.status === 404 ? (
          <EmptyState
            variant="card"
            icon={LightbulbIcon}
            title="Super admins only"
            description="Opportunities are open to platform super admins."
          />
        ) : (
          <LoadError what="Opportunities" error={q.error} />
        )}
      </OpsBody>
    );
  const p = q.data;
  return (
    <OpsBody data-slot="opportunities">
      <PipelineStrip pipeline={p.pipeline} />
      <div
        role="toolbar"
        aria-label="Filters"
        className="flex flex-wrap items-center gap-2"
        data-slot="opportunity-filters"
      >
        <NativeSelect
          size="sm"
          aria-label="Company"
          value={company}
          onChange={(e) => setCompany(e.target.value)}
        >
          <NativeSelectOption value="">All companies</NativeSelectOption>
          {p.companies.map((c) => (
            <NativeSelectOption key={c.id} value={c.id}>
              {c.name}
            </NativeSelectOption>
          ))}
        </NativeSelect>
        <NativeSelect
          size="sm"
          aria-label="Stage"
          value={stage}
          onChange={(e) => setStage(e.target.value as Stage | "")}
        >
          <NativeSelectOption value="">All stages</NativeSelectOption>
          {STAGES.map((s) => (
            <NativeSelectOption key={s} value={s}>
              {STAGE_LABEL[s]}
            </NativeSelectOption>
          ))}
        </NativeSelect>
      </div>
      {p.data.length === 0 ? (
        <EmptyState
          variant="card"
          icon={LightbulbIcon}
          title={stage || company ? "No opportunities match" : "No opportunities yet"}
          description="Skillsmith files one when a DE keeps failing or gets corrected."
        />
      ) : (
        <ol className="flex flex-col divide-y divide-subtle" data-slot="opportunity-list">
          {p.data.map((o) => (
            <li key={o.id} className="py-3 first:pt-0">
              <OpportunityRow
                opp={o}
                onDialog={setDialog}
                onMove={(stage) => actions.stage.mutate({ opp: o, stage })}
                onAddHelper={(quote) =>
                  actions.quoteStatus.mutate({ opp: o, quote, status: "added" })
                }
                pending={actions.stage.isPending || actions.quoteStatus.isPending}
              />
            </li>
          ))}
        </ol>
      )}
      {dialog?.kind === "draft" ? (
        <DraftOfferDialog api={api} opp={dialog.opp} onClose={() => setDialog(null)} />
      ) : null}
      {dialog?.kind === "quote" ? (
        <QuoteDialog api={api} opp={dialog.opp} onClose={() => setDialog(null)} />
      ) : null}
      {dialog?.kind === "stage" ? (
        <StageDialog
          api={api}
          opp={dialog.opp}
          stage={dialog.stage}
          onClose={() => setDialog(null)}
        />
      ) : null}
      {dialog?.kind === "cancel" ? (
        <AlertDialog open onOpenChange={(o) => !o && setDialog(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Cancel the quote for {dialog.quote.helper_name}?</AlertDialogTitle>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Keep</AlertDialogCancel>
              <AlertDialogAction
                variant="destructive"
                onClick={() => {
                  actions.quoteStatus.mutate({
                    opp: dialog.opp,
                    quote: dialog.quote,
                    status: "canceled",
                  });
                  setDialog(null);
                }}
              >
                Cancel quote
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      ) : null}
    </OpsBody>
  );
}

/** Suggested → Offered → Accepted / Declined → Added, with counts, and the revenue. */
function PipelineStrip({ pipeline }: { pipeline: Pipeline["pipeline"] }) {
  const c = pipeline.counts;
  const step = (s: Stage) => (
    <span data-stage={s} className="inline-flex items-center gap-1.5">
      <Badge variant={STAGE_BADGE[s]}>{STAGE_LABEL[s]}</Badge>
      <span className="tabular-nums" data-slot="stage-count">
        {c[s]}
      </span>
    </span>
  );
  const arrow = <ArrowRightIcon aria-hidden="true" className="size-3.5 text-muted-foreground" />;
  return (
    <section
      aria-label="Pipeline"
      data-slot="opportunity-pipeline"
      className="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-lg border border-subtle px-4 py-3 text-sm"
    >
      {step("suggested")}
      {arrow}
      {step("offered")}
      {arrow}
      {step("accepted")}
      <span className="text-muted-foreground">/</span>
      {step("declined")}
      {arrow}
      {step("added")}
      <span className="text-xs text-muted-foreground tabular-nums" data-stage="dismissed">
        {c.dismissed} dismissed
      </span>
      <span className="ml-auto inline-flex items-center gap-1.5" data-slot="opportunity-revenue">
        <span className="text-muted-foreground">Revenue</span>
        <span className="font-semibold tabular-nums">
          {pipeline.revenue.length
            ? pipeline.revenue.map((r) => formatMoney(r.amount_minor, r.currency)).join(" · ")
            : formatMoney(0, "USD")}
        </span>
        <InfoTip label="About revenue">
          Accepted and added: the amount entered, else the quote's one-off plus first month.
        </InfoTip>
      </span>
    </section>
  );
}

function useOpportunityActions(api: Api) {
  const client = useQueryClient();
  const refresh = () => client.invalidateQueries({ queryKey: opsKeys.opportunities() });
  const stage = useMutation({
    mutationFn: (v: { opp: Opportunity; stage: Move; amount_minor?: number }) =>
      api.client.operator.opportunities.stage({
        params: { company_id: v.opp.company.id, opportunity_id: v.opp.id },
        body: {
          stage: v.stage,
          ...(v.amount_minor !== undefined ? { amount_minor: v.amount_minor } : {}),
        },
      }),
    onSuccess: (o) => toast.add({ title: `${STAGE_LABEL[o.stage]}: ${o.title}` }),
    onError: (e) => toast.add({ title: errorText(e, "Not moved"), type: "error" }),
    onSettled: refresh,
  });
  const quoteStatus = useMutation({
    mutationFn: (v: { opp: Opportunity; quote: Quote; status: "added" | "canceled" }) =>
      api.client.operator.helperOffers.status({
        params: { company_id: v.opp.company.id, offer_id: v.quote.id },
        body: { status: v.status },
      }),
    onSuccess: (q) =>
      toast.add({
        title: q.status === "added" ? `Helper added: ${q.helper_name}` : "Quote canceled",
      }),
    onError: (e) => toast.add({ title: errorText(e, "Not changed"), type: "error" }),
    onSettled: refresh,
  });
  return { stage, quoteStatus };
}

const open = (s: Stage) => s === "suggested" || s === "offered";

/** One opportunity: the summary line, hover actions, and the details on expand. */
function OpportunityRow({
  opp: o,
  onDialog,
  onMove,
  onAddHelper,
  pending,
}: {
  opp: Opportunity;
  onDialog: (d: Dialogs) => void;
  onMove: (stage: Move) => void;
  onAddHelper: (quote: Quote) => void;
  pending: boolean;
}) {
  const [expanded, setExpanded] = useState(false);
  const q = o.helper_offer;
  const quoteLive = q && (q.status === "open" || q.status === "building");
  const counts = Object.entries(o.evidence_counts).filter(([, n]) => n > 0);
  const detailsId = useId();
  return (
    <article
      className="group/row flex min-w-0 flex-col gap-1.5"
      data-slot="opportunity"
      data-opportunity={o.id}
      data-stage={o.stage}
    >
      <div className="flex min-w-0 items-start gap-2">
        <Button
          variant="ghost"
          size="icon-sm"
          aria-expanded={expanded}
          aria-controls={detailsId}
          aria-label={`${expanded ? "Hide" : "Show"} details of “${o.title}”`}
          onClick={() => setExpanded((x) => !x)}
        >
          {expanded ? (
            <ChevronDownIcon aria-hidden="true" />
          ) : (
            <ChevronRightIcon aria-hidden="true" />
          )}
        </Button>
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <div className="flex min-w-0 flex-wrap items-center gap-2">
            <CompanyTile name={o.company.name} size={20} />
            <span className="text-sm font-medium">{o.company.name}</span>
            {o.de ? (
              <span className="inline-flex items-center gap-1 text-sm">
                <Badge variant="outline">DE</Badge>
                {o.de.name}
              </span>
            ) : null}
            <Badge variant={STAGE_BADGE[o.stage]} data-slot="opportunity-stage">
              {STAGE_LABEL[o.stage]}
            </Badge>
            {q ? (
              <Badge variant={q.status === "open" ? "warning" : "neutral"} data-slot="quote-status">
                {QUOTE_LABEL[q.status]}
              </Badge>
            ) : null}
          </div>
          <h3 className="text-sm font-semibold">{o.title}</h3>
          <div className="flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
            <Badge variant="neutral">{o.suggestion_kind === "helper" ? "Helper" : "Skill"}</Badge>
            <span>{o.suggested_name}</span>
            <span aria-hidden="true">·</span>
            <span>{CONFIDENCE[o.confidence]}</span>
            {counts.slice(0, 3).map(([k, n]) => (
              <Badge key={k} variant="outline" data-slot="evidence-count">
                {n} {words(k).toLowerCase()}
              </Badge>
            ))}
          </div>
        </div>
        <RowActions className="shrink-0">
          {open(o.stage) && !o.offer.sent_at ? (
            <RowAction
              icon={MailIcon}
              label={`Draft offer for “${o.title}”`}
              tooltip="Draft offer"
              onClick={() => onDialog({ kind: "draft", opp: o })}
            />
          ) : null}
          {open(o.stage) && !quoteLive ? (
            <RowAction
              icon={ReceiptIcon}
              label={`Send quote for “${o.title}”`}
              tooltip="Send quote"
              onClick={() => onDialog({ kind: "quote", opp: o })}
            />
          ) : null}
          {open(o.stage) && !quoteLive ? (
            <>
              <RowAction
                icon={ThumbsUpIcon}
                label={`Mark “${o.title}” accepted`}
                tooltip="Mark accepted"
                onClick={() => onDialog({ kind: "stage", opp: o, stage: "accepted" })}
              />
              <RowAction
                icon={ThumbsDownIcon}
                label={`Mark “${o.title}” declined`}
                tooltip="Mark declined"
                onClick={() => onDialog({ kind: "stage", opp: o, stage: "declined" })}
              />
            </>
          ) : null}
          {(o.stage === "accepted" || open(o.stage)) && !quoteLive ? (
            <RowAction
              icon={PackageCheckIcon}
              label={`Mark “${o.title}” added`}
              tooltip="Mark added"
              onClick={() => onDialog({ kind: "stage", opp: o, stage: "added" })}
            />
          ) : null}
          {q?.status === "building" ? (
            <RowAction
              icon={PackageCheckIcon}
              label={`Mark helper ${q.helper_name} added`}
              tooltip="Mark helper Added"
              disabled={pending}
              onClick={() => onAddHelper(q)}
            />
          ) : null}
          {q?.status === "open" ? (
            <RowAction
              icon={BanIcon}
              label={`Cancel the quote for ${q.helper_name}`}
              tooltip="Cancel quote"
              onClick={() => onDialog({ kind: "cancel", opp: o, quote: q })}
            />
          ) : null}
          {open(o.stage) && !quoteLive ? (
            <RowAction
              icon={XIcon}
              label={`Dismiss “${o.title}”`}
              tooltip="Dismiss"
              disabled={pending}
              onClick={() => onMove("dismissed")}
            />
          ) : null}
          {o.stage === "dismissed" || o.stage === "declined" ? (
            <RowAction
              icon={Undo2Icon}
              label={`Reopen “${o.title}”`}
              tooltip="Reopen"
              disabled={pending}
              onClick={() => onMove("suggested")}
            />
          ) : null}
        </RowActions>
      </div>
      {expanded ? (
        <div id={detailsId} className="ml-9">
          <OpportunityDetails opp={o} />
        </div>
      ) : null}
    </article>
  );
}

function OpportunityDetails({ opp: o }: { opp: Opportunity }) {
  const q = o.helper_offer;
  const rows: [string, ReactNode][] = [
    [
      "Problem",
      <span key="problem" className="whitespace-pre-wrap">
        {o.problem}
      </span>,
    ],
    [
      "Counts",
      Object.entries(o.evidence_counts)
        .map(([k, n]) => `${words(k)}: ${n}`)
        .join(" · ") || "—",
    ],
    [
      "Examples",
      o.evidence.length ? (
        <ul key="examples" className="flex flex-col gap-0.5">
          {o.evidence.map((e) => (
            <li key={`${e.kind}:${e.id}`}>
              <span className="font-mono text-xs">
                {e.kind} {e.id.slice(0, 8)}
              </span>
              {e.note ? <span className="text-muted-foreground"> — {e.note}</span> : null}
            </li>
          ))}
        </ul>
      ) : (
        "—"
      ),
    ],
    [
      o.suggestion_kind === "helper" ? "Helper" : "Skill",
      <span key="suggested">
        <span className="font-medium">{o.suggested_name}</span> — {o.suggested_what}
      </span>,
    ],
    ["Benefit", o.expected_benefit],
    ["Source", o.source === "skillsmith" ? "Skillsmith" : "Client asked"],
    [
      "Offer",
      o.offer.sent_at
        ? `Sent ${when(o.offer.sent_at)} to ${o.offer.to.length} ${o.offer.to.length === 1 ? "person" : "people"}: ${o.offer.subject ?? ""}`
        : o.offer.edited_at
          ? `Draft saved ${when(o.offer.edited_at)}`
          : "Not sent",
    ],
    [
      "Quote",
      q
        ? `${q.helper_name} · ${quotePrice(q)} · ${QUOTE_LABEL[q.status]}${q.invoice ? ` · ${q.invoice.number}` : ""}`
        : "None",
    ],
  ];
  if (o.amount_minor !== null) rows.push(["Amount", formatMoney(o.amount_minor, o.currency)]);
  return (
    <dl
      className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 rounded-md bg-sunken p-3 text-sm"
      data-slot="opportunity-details"
    >
      {rows.map(([k, v]) => (
        <Fragment key={k}>
          <dt className="text-muted-foreground">{k}</dt>
          <dd className="min-w-0">{v}</dd>
        </Fragment>
      ))}
      <dt className="text-muted-foreground">Filed</dt>
      <dd>
        <RowSub>{when(o.created_at)}</RowSub>
      </dd>
    </dl>
  );
}

/** The offer email: subject, body, recipients; Save, or Send after a confirm. */
function DraftOfferDialog({
  api,
  opp,
  onClose,
}: {
  api: Api;
  opp: Opportunity;
  onClose: () => void;
}) {
  const client = useQueryClient();
  const detail = useQuery(opportunityQuery(api, opp.company.id, opp.id));
  const [subject, setSubject] = useState(opp.offer.subject ?? "");
  const [body, setBody] = useState(opp.offer.body ?? "");
  const [to, setTo] = useState<string[] | null>(opp.offer.to.length ? opp.offer.to : null);
  const [confirm, setConfirm] = useState(false);
  const id = useId();
  const people = detail.data?.people ?? [];
  // Nothing chosen yet: the company's owners.
  const chosen = to ?? people.filter((p) => p.role === "owner").map((p) => p.membership_id);
  const params = { company_id: opp.company.id, opportunity_id: opp.id };
  const refresh = () => client.invalidateQueries({ queryKey: opsKeys.opportunities() });
  const save = useMutation({
    mutationFn: () =>
      api.client.operator.opportunities.draft({
        params,
        body: { subject: subject.trim(), body: body.trim(), to: chosen },
      }),
  });
  const send = useMutation({
    mutationFn: () => api.client.operator.opportunities.send({ params }),
  });
  const ready = subject.trim() !== "" && body.trim() !== "" && chosen.length > 0;
  const busy = save.isPending || send.isPending;
  const error = save.error ?? send.error;
  const onSave = (e: FormEvent) => {
    e.preventDefault();
    if (!ready) return;
    save.mutate(undefined, {
      onSuccess: () => {
        toast.add({ title: "Draft saved" });
        void refresh();
        onClose();
      },
    });
  };
  const onSend = () => {
    setConfirm(false);
    save.mutate(undefined, {
      onSuccess: () =>
        send.mutate(undefined, {
          onSuccess: () => {
            toast.add({
              title: `Offer sent to ${chosen.length} ${chosen.length === 1 ? "person" : "people"} at ${opp.company.name}`,
            });
            void refresh();
            onClose();
          },
          onSettled: () => void refresh(),
        }),
    });
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-[600px]" data-slot="draft-offer-dialog">
        <form onSubmit={onSave} className="grid gap-4">
          <DialogHeader>
            <DialogTitle>Draft offer</DialogTitle>
            <RowSub>
              {opp.company.name}
              {opp.de ? ` · DE ${opp.de.name}` : ""}
            </RowSub>
          </DialogHeader>
          <Field>
            <FieldLabel htmlFor={`${id}-subject`}>Subject</FieldLabel>
            <Input
              id={`${id}-subject`}
              value={subject}
              maxLength={200}
              onChange={(e) => setSubject(e.target.value)}
            />
          </Field>
          <Field>
            <FieldLabel htmlFor={`${id}-body`}>Message</FieldLabel>
            <Textarea
              id={`${id}-body`}
              value={body}
              rows={7}
              maxLength={5000}
              className="max-h-64"
              onChange={(e) => setBody(e.target.value)}
            />
          </Field>
          <fieldset className="m-0 flex flex-col gap-2 border-0 p-0" data-slot="offer-recipients">
            <legend className="mb-1 inline-flex items-center gap-1 text-sm font-medium">
              To
              <InfoTip label="About recipients">Owners are chosen first. Up to 20 people.</InfoTip>
            </legend>
            {detail.isPending ? (
              <Loading label="Loading people" rows={2} />
            ) : detail.isError ? (
              <LoadError what="People" error={detail.error} />
            ) : (
              <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 max-sm:grid-cols-1">
                {people.map((p) => {
                  const on = chosen.includes(p.membership_id);
                  return (
                    <div key={p.membership_id} className="flex min-w-0 items-center gap-2">
                      <Checkbox
                        id={`${id}-${p.membership_id}`}
                        checked={on}
                        onCheckedChange={(v) =>
                          setTo(
                            v === true
                              ? [...chosen, p.membership_id].slice(0, 20)
                              : chosen.filter((x) => x !== p.membership_id),
                          )
                        }
                      />
                      <Label
                        htmlFor={`${id}-${p.membership_id}`}
                        className="min-w-0 truncate font-normal"
                        title={p.email ?? undefined}
                      >
                        {p.name}
                        <span className="text-muted-foreground"> · {words(p.role)}</span>
                      </Label>
                    </div>
                  );
                })}
              </div>
            )}
          </fieldset>
          {error ? <FieldError>{errorText(error, "The offer was not saved.")}</FieldError> : null}
          <DialogFooter>
            <DialogClose render={<Button variant="secondary" type="button" />}>Cancel</DialogClose>
            <Button type="submit" variant="secondary" disabled={!ready || busy}>
              Save
            </Button>
            <Button type="button" disabled={!ready || busy} onClick={() => setConfirm(true)}>
              <MailIcon aria-hidden="true" />
              Send
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
      <AlertDialog open={confirm} onOpenChange={(o) => !o && setConfirm(false)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Send to {chosen.length} {chosen.length === 1 ? "person" : "people"} at{" "}
              {opp.company.name}?
            </AlertDialogTitle>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={onSend}>Send</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Dialog>
  );
}

/** A dollars input ("$" in front), the text kept as typed. */
function DollarsField({
  id,
  label,
  value,
  onChange,
  invalid,
  suffix,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
  invalid: boolean;
  suffix?: string;
}) {
  return (
    <Field data-invalid={invalid}>
      <FieldLabel htmlFor={id}>{label}</FieldLabel>
      <InputGroup>
        <InputGroupAddon>$</InputGroupAddon>
        <InputGroupInput
          id={id}
          inputMode="decimal"
          value={value}
          aria-invalid={invalid}
          placeholder="0"
          onChange={(e) => onChange(e.target.value)}
        />
        {suffix ? <InputGroupAddon align="inline-end">{suffix}</InputGroupAddon> : null}
      </InputGroup>
    </Field>
  );
}

/** "" → 0; else the cents, or null when it is not an amount. */
const centsOrZero = (s: string) => (s.trim() === "" ? 0 : parseDollarsToCents(s));

/** A paid helper quote: name, what it does, one-off and monthly prices. */
function QuoteDialog({ api, opp, onClose }: { api: Api; opp: Opportunity; onClose: () => void }) {
  const client = useQueryClient();
  const [name, setName] = useState(opp.suggested_name);
  const [description, setDescription] = useState(opp.suggested_what);
  const [oneOff, setOneOff] = useState("");
  const [monthly, setMonthly] = useState("");
  const id = useId();
  const oneOffMinor = centsOrZero(oneOff);
  const monthlyMinor = centsOrZero(monthly);
  const create = useMutation({
    mutationFn: () =>
      api.client.operator.helperOffers.create({
        params: { company_id: opp.company.id },
        body: {
          helper_name: name.trim(),
          ...(description.trim() ? { description: description.trim() } : {}),
          ...(opp.de ? { de_membership_id: opp.de.membership_id } : {}),
          one_off_minor: oneOffMinor ?? 0,
          monthly_minor: monthlyMinor ?? 0,
          currency: opp.currency,
          opportunity_id: opp.id,
        },
      }),
    onSuccess: (q) => {
      toast.add({ title: `Quote sent: ${q.helper_name}`, description: quotePrice(q) });
      void client.invalidateQueries({ queryKey: opsKeys.opportunities() });
      onClose();
    },
  });
  const bad = oneOffMinor === null || monthlyMinor === null;
  const ready = !bad && name.trim() !== "" && (oneOffMinor ?? 0) + (monthlyMinor ?? 0) > 0;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-slot="quote-dialog">
        <form
          className="grid gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            if (ready) create.mutate();
          }}
        >
          <DialogHeader>
            <DialogTitle className="inline-flex items-center gap-1">
              Send quote
              <InfoTip label="About quotes">
                The company's owners and admins approve it on Billing. Approved: the one-off is
                invoiced and the monthly price joins the subscription.
              </InfoTip>
            </DialogTitle>
            <RowSub>
              {opp.company.name}
              {opp.de ? ` · DE ${opp.de.name}` : ""}
            </RowSub>
          </DialogHeader>
          <Field>
            <FieldLabel htmlFor={`${id}-name`}>Helper name</FieldLabel>
            <Input
              id={`${id}-name`}
              value={name}
              maxLength={120}
              onChange={(e) => setName(e.target.value)}
            />
          </Field>
          <Field>
            <FieldLabel htmlFor={`${id}-what`}>What it does</FieldLabel>
            <Textarea
              id={`${id}-what`}
              value={description}
              rows={3}
              maxLength={2000}
              onChange={(e) => setDescription(e.target.value)}
            />
          </Field>
          <div className="grid grid-cols-2 gap-4 max-sm:grid-cols-1">
            <DollarsField
              id={`${id}-oneoff`}
              label="One-off"
              value={oneOff}
              onChange={setOneOff}
              invalid={oneOffMinor === null}
            />
            <DollarsField
              id={`${id}-monthly`}
              label="Monthly"
              value={monthly}
              onChange={setMonthly}
              invalid={monthlyMinor === null}
              suffix="/mo"
            />
          </div>
          {bad ? <FieldError>Enter amounts like 500 or 99.50.</FieldError> : null}
          {create.isError ? (
            <FieldError>{errorText(create.error, "The quote was not sent.")}</FieldError>
          ) : null}
          <DialogFooter>
            <DialogClose render={<Button variant="secondary" type="button" />}>Cancel</DialogClose>
            <Button type="submit" disabled={!ready || create.isPending}>
              <ReceiptIcon aria-hidden="true" />
              Send quote
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/** Mark accepted / declined / added, with the amount it sold for (accepted and added). */
function StageDialog({
  api,
  opp,
  stage,
  onClose,
}: {
  api: Api;
  opp: Opportunity;
  stage: "accepted" | "declined" | "added";
  onClose: () => void;
}) {
  const client = useQueryClient();
  const [amount, setAmount] = useState(
    opp.amount_minor !== null ? centsToDollarsText(opp.amount_minor) : "",
  );
  const id = useId();
  const withAmount = stage !== "declined";
  const cents = amount.trim() === "" ? undefined : parseDollarsToCents(amount);
  const move = useMutation({
    mutationFn: () =>
      api.client.operator.opportunities.stage({
        params: { company_id: opp.company.id, opportunity_id: opp.id },
        body: {
          stage,
          ...(withAmount && typeof cents === "number"
            ? { amount_minor: cents, currency: opp.currency }
            : {}),
        },
      }),
    onSuccess: (o) => {
      toast.add({ title: `${STAGE_LABEL[o.stage]}: ${o.title}` });
      void client.invalidateQueries({ queryKey: opsKeys.opportunities() });
      onClose();
    },
  });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-slot="stage-dialog">
        <form
          className="grid gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            if (cents !== null) move.mutate();
          }}
        >
          <DialogHeader>
            <DialogTitle>Mark {STAGE_LABEL[stage].toLowerCase()}</DialogTitle>
            <RowSub>
              {opp.company.name} · {opp.title}
            </RowSub>
          </DialogHeader>
          {withAmount ? (
            <DollarsField
              id={`${id}-amount`}
              label="Amount (optional)"
              value={amount}
              onChange={setAmount}
              invalid={cents === null}
            />
          ) : null}
          {move.isError ? <FieldError>{errorText(move.error, "Not moved.")}</FieldError> : null}
          <DialogFooter>
            <DialogClose render={<Button variant="secondary" type="button" />}>Cancel</DialogClose>
            <Button type="submit" disabled={cents === null || move.isPending}>
              Mark {STAGE_LABEL[stage].toLowerCase()}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
